import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { ButtonStateEnum } from '../../lib/types';
import { isEmail, isEmptyString } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

import { updateEmailTemplate } from './emailTemplates.repository';
import { EmailSettings, EmailTemplate } from './types';

const EmailSettingsInitialState: EmailSettings = {
  emailSubject: '',
  senderName: '',
  senderEmail: '',
  replyEmail: '',
};

export type EmailSettingsContextValue = {
  discardChanges: () => void;
  emailSettingsError: EmailSettings;
  emailSettings: EmailSettings;
  isUnsaved: boolean;
  saveButtonState: ButtonStateEnum;
  saveSettings: () => Promise<boolean>;
  setEmailTemplate: (emailTemplate: EmailTemplate) => void;
  setEmailSettingsError: (emailSettingsErrors: EmailSettings) => void;
  setEmailSettings: (emailSettings: EmailSettings) => void;
  setIsUnsaved: (isUnsaved: boolean) => void;
  setIsValid: (isValid: boolean) => void;
  setSaveButtonState: (saveButtonState: ButtonStateEnum) => void;
  isSettingsEmailInvalid: (invalidFormErrors: EmailSettings, field: keyof EmailSettings) => boolean;
  isSettingsFormValid: (setErrors: boolean) => boolean;
};

export const initialEmailSettingsContext: EmailSettingsContextValue = {
  discardChanges: () => {},
  emailSettingsError: { ...EmailSettingsInitialState },
  emailSettings: { ...EmailSettingsInitialState },
  isUnsaved: false,
  saveButtonState: 'Default',
  saveSettings: () => Promise.resolve(true),
  setEmailTemplate: () => {},
  setEmailSettingsError: () => {},
  setEmailSettings: () => {},
  setIsUnsaved: () => {},
  setIsValid: () => {},
  setSaveButtonState: () => {},
  isSettingsEmailInvalid: () => true,
  isSettingsFormValid: () => true,
};

export const EmailSettingsContext = createContext<EmailSettingsContextValue>(
  initialEmailSettingsContext,
);

export const EmailSettingsContextProvider: React.FC<{}> = ({ children }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate>();

  const [isUnsaved, setIsUnsaved] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);

  const [emailSettingsError, setEmailSettingsError] = useState<EmailSettings>(EmailSettingsInitialState);
  const [emailSettings, setEmailSettings] = useState<EmailSettings>(EmailSettingsInitialState);
  const [saveButtonState, setSaveButtonState] = useState<ButtonStateEnum>('Default');

  const updateEmailTemplateSettingsMutation = useMutation(
    (payload: EmailTemplate) => {
      if (emailTemplate?.id) {
        return updateEmailTemplate(emailTemplate.id, payload);
      } else {
        return Promise.reject();
      }
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('getEmailTemplate');
      },
    },
  );

  useEffect(() => {
    if (emailTemplate) {
      setEmailSettings({
        emailSubject: emailTemplate.emailSubject,
        senderName: emailTemplate.senderName,
        senderEmail: emailTemplate.senderEmail,
        replyEmail: emailTemplate.replyEmail,
      });
    }
  }, [emailTemplate]);

  const discardChanges = () => {
    if (emailTemplate) {
      setEmailSettings(emailTemplate);
      setEmailSettingsError(EmailSettingsInitialState);
    }
    setIsUnsaved(false);
  };

  const isSettingsEmailInvalid = useCallback((invalidFormErrors: EmailSettings, field: keyof EmailSettings) => {
    let isError = false;

    invalidFormErrors[field] = '';

    if (
      !isEmptyString(emailSettings[field]) &&
      !isEmail(emailSettings[field])
    ) {
      invalidFormErrors[field] = t(
        'side_bar.settings.errors.invalid_email',
      );
      isError = true;
    }
    return isError;
  }, [emailSettings, t]);

  /**
   * Validates the settings form.
   * 
   * Form is valid EVEN IF:
   * - Sender name is empty;
   * - Sender email is empty.
   * 
   * Form is not valid if and only if:
   * - Sender email is non-empty AND is not in a correct email format OR
   * - Reply email is non-empty AND is not in a correct email format
   */
  const isSettingsFormValid = useCallback((setErrors: boolean) => {
    const invalidFormErrors = {
      emailSubject: '',
      senderName: '',
      senderEmail: '',
      replyEmail: '',
    };

    let isError = false;

    isError = isSettingsEmailInvalid(invalidFormErrors, 'senderEmail');
    isError = isSettingsEmailInvalid(invalidFormErrors, 'replyEmail') || isError;

    if (setErrors) {
      setEmailSettingsError(invalidFormErrors);
    }

    return !isError;
  }, [isSettingsEmailInvalid, setEmailSettingsError]);

  const saveSettings = async () => {
    if (emailTemplate && isValid) {
      setIsUnsaved(false);
      setSaveButtonState('Loading');
      await updateEmailTemplateSettingsMutation.mutateAsync({
        ...emailTemplate,
        emailSubject: emailSettings.emailSubject,
        senderName: emailSettings.senderName,
        senderEmail: emailSettings.senderEmail,
        replyEmail: emailSettings.replyEmail,
      });
      setSaveButtonState('Loaded');
      return true;
    }
    return false;
  };

  return (
    <EmailSettingsContext.Provider
      value={{
        discardChanges,
        emailSettingsError,
        emailSettings,
        isUnsaved,
        saveButtonState,
        saveSettings,
        setEmailTemplate,
        setEmailSettingsError,
        setEmailSettings,
        setIsUnsaved,
        setIsValid,
        setSaveButtonState,
        isSettingsEmailInvalid,
        isSettingsFormValid,
      }}
    >
      {children}
    </EmailSettingsContext.Provider>
  );
};
